/**
 * LoginPage with integrated error boundaries
 * Wraps the main LoginPage component with comprehensive error handling
 */

import React from 'react';
import { LoginPage } from './LoginPage';
import { AuthErrorBoundary as LoginErrorBoundary } from './AuthErrorBoundary';
import { AuthErrorBoundary as NFCErrorBoundary } from './AuthErrorBoundary';
import { authErrorReporting } from '../services/AuthErrorReportingService';

export const LoginPageWithErrorBoundary: React.FC = () => {
  const [formKey, setFormKey] = React.useState(0);
  const [nfcKey, setNfcKey] = React.useState(0);

  const handleLoginError = React.useCallback((error: Error) => {
    console.error('Login page error:', error);
    
    // Report error to monitoring service
    authErrorReporting.reportError(error, 'LoginPage', 'page_error', {
      severity: 'high',
    });
  }, []);

  const handleFormReset = React.useCallback(() => {
    // Reset form by changing key (force remount)
    setFormKey(prev => prev + 1);
  }, []);

  const handleNFCError = React.useCallback((error: Error, uid?: string) => {
    console.error('NFC error on login page:', error, uid);
    
    // Report NFC error
    authErrorReporting.reportError(error, 'LoginPage', 'nfc_error', {
      severity: 'medium',
    });
  }, []);

  const handleNFCReset = React.useCallback(() => {
    // Reset NFC components
    setNfcKey(prev => prev + 1);
  }, []);

  return (
    <LoginErrorBoundary
      onLoginError={handleLoginError}
      onFormReset={handleFormReset}
    >
      <NFCErrorBoundary
        onNFCError={handleNFCError}
        onNFCReset={handleNFCReset}
      >
        <LoginPage key={`login-${formKey}-${nfcKey}`} />
      </NFCErrorBoundary>
    </LoginErrorBoundary>
  );
};

export default LoginPageWithErrorBoundary;